import { useCallback, useEffect, useRef, useState } from "react";
import "altcha";
import type {} from "altcha/types/react";
import type { State } from "altcha/types";

type AltchaPending = {
	resolve: (payload: string) => void;
	reject: (err: Error) => void;
	timeoutId: number;
};

/**
 * Low-level hook that manages a single Altcha widget instance.
 * Listens for statechange events and exposes solveCaptcha() to
 * programmatically trigger verification.
 *
 * Failures stay silent here. Auth pages already surface captcha problems
 * inline when the user submits; background auto-refresh must not toast.
 */
export function useAltcha(params?: { enabled?: boolean }) {
	const widgetRef = useRef<
		| (HTMLElement & {
				verify?: () => Promise<{ payload: string } | null>;
				reset?: (state?: State) => void;
		  })
		| null
	>(null);
	const [payload, setPayload] = useState("");

	// A single in-flight verify is tracked here. Concurrent solveCaptcha()
	// calls (e.g. caused by React.StrictMode's double-invoked effects, or by
	// rapid retries) are coalesced onto this same promise, so we never start
	// two parallel verify() calls on the underlying widget. Starting a second
	// verify aborts the first via the widget's internal AbortController,
	// which surfaces as a spurious "Failed to find solution." error.
	const pendingRef = useRef<AltchaPending | null>(null);
	const inflightRef = useRef<Promise<string> | null>(null);

	const enabled = params?.enabled ?? true;

	const settlePending = useCallback(
		(handler: (pending: AltchaPending) => void) => {
			const pending = pendingRef.current;
			pendingRef.current = null;
			inflightRef.current = null;
			if (!pending) return;
			window.clearTimeout(pending.timeoutId);
			handler(pending);
		},
		[],
	);

	useEffect(() => {
		if (!enabled) return;
		const el = widgetRef.current;
		if (!el) return;

		const handleStateChange = (e: Event) => {
			const detail = (e as CustomEvent).detail as
				| { state: string; payload: string | null }
				| undefined;
			if (!detail) return;

			if (detail.state === "verified" && detail.payload) {
				const verifiedPayload = detail.payload;
				setPayload(verifiedPayload);
				settlePending((p) => p.resolve(verifiedPayload));
			} else if (detail.state === "error" || detail.state === "expired") {
				setPayload("");
				settlePending((p) => p.reject(new Error(`Captcha ${detail.state}`)));
			}
		};

		el.addEventListener("statechange", handleStateChange);
		return () => {
			el.removeEventListener("statechange", handleStateChange);
		};
	}, [enabled, settlePending]);

	const solveCaptcha = useCallback(async (): Promise<string> => {
		if (inflightRef.current) return inflightRef.current;

		const el = widgetRef.current;
		if (!el?.verify) {
			throw new Error("Altcha widget not ready");
		}

		// Note: we intentionally do NOT call el.reset() here. The widget's
		// verify() already performs an internal reset, and calling reset()
		// explicitly would abort an in-flight verify (which is what caused
		// the "Failed to find solution." race in the first place).
		const promise = new Promise<string>((resolve, reject) => {
			const timeoutId = window.setTimeout(() => {
				settlePending((p) => p.reject(new Error("Captcha timeout")));
			}, 60_000);

			pendingRef.current = { resolve, reject, timeoutId };

			if (el.verify) {
				el.verify().catch((err: unknown) => {
					settlePending((p) =>
						p.reject(err instanceof Error ? err : new Error(String(err))),
					);
				});
			} else {
				settlePending((p) => p.reject(new Error("Altcha widget not ready")));
			}
		});

		inflightRef.current = promise;
		return promise;
	}, [settlePending]);

	const reset = useCallback(() => {
		widgetRef.current?.reset?.();
		setPayload("");
		settlePending((p) => p.reject(new Error("Captcha reset")));
	}, [settlePending]);

	return { widgetRef, solveCaptcha, reset, payload };
}
